import React, { useMemo } from 'react';
import { formatApiDate } from '../utils/security';
import { getApiBaseUrl } from '../services/apiClient';
import './SiparisDetayModal.css';
import { FieldLabel } from './FieldLabel';
import SiparisNotuCard from './SiparisNotuCard';
import { toTurkishUpperCase } from '../utils/turkishCase';
import {
  isGarantiOtomatikTelafiSiparis,
  getOtomatikTelafiTipLabel,
  referansBirimFiyat,
} from '../utils/siparisDisplayHelpers';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '—';
  return `${parseFloat(amount).toFixed(2)} ₺`;
};

const EYLEM_ETIKET = {
  SiparisOlusturuldu: 'Sipariş oluşturuldu',
  SiparisOnaylandi: 'Sipariş onaylandı',
  SiparisIptal: 'Sipariş iptal edildi',
  KargoBilgisiGirildi: 'Kargo bilgisi girildi',
  FaturaBilgisiGirildi: 'Fatura bilgisi girildi',
  SevkIrsaliyesiGirildi: 'Sevk irsaliyesi girildi',
  KargoTeslimAlindi: 'Kargo teslim alındı',
  OtomatikSiparisOlusturuldu: 'Otomatik telafi belgesi oluşturuldu',
};

function resolveUploadUrl(url) {
  if (!url || typeof url !== 'string') return '';
  if (/^https?:\/\//i.test(url)) return url;
  const base = getApiBaseUrl().replace(/\/$/, '');
  return `${base}${url.startsWith('/') ? url : `/${url}`}`;
}

const PozisyonTable = ({
  pozisyonlar,
  garantiDahilinde,
  konsinye,
  otomatikTelafi = false,
  showFiyat = true,
}) => {
  const showTutar = showFiyat && !garantiDahilinde && !konsinye && !otomatikTelafi;
  const showTelafiCols = otomatikTelafi;

  return (
    <table className="admins-table">
      <thead>
        <tr>
          <th>{toTurkishUpperCase('Parça Adı')}</th>
          <th>{toTurkishUpperCase('Kod')}</th>
          <th style={{ textAlign: 'center' }}>{toTurkishUpperCase('Adet')}</th>
          {showTelafiCols && (
            <th style={{ textAlign: 'right' }}>{toTurkishUpperCase('Referans birim fiyat')}</th>
          )}
          {showTelafiCols && (
            <th style={{ textAlign: 'right' }}>{toTurkishUpperCase('Bedel')}</th>
          )}
          {showTutar && <th style={{ textAlign: 'right' }}>{toTurkishUpperCase('Tutar')}</th>}
        </tr>
      </thead>
      <tbody>
        {(pozisyonlar || []).map((p, idx) => (
          <tr key={idx}>
            <td>{p.parcaAdi || '—'}</td>
            <td>{p.parcaKodu || '—'}</td>
            <td style={{ textAlign: 'center' }}>{p.adet || 1}</td>
            {showTelafiCols && (
              <td style={{ textAlign: 'right' }}>
                {referansBirimFiyat(p) != null ? formatCurrency(referansBirimFiyat(p)) : '—'}
              </td>
            )}
            {showTelafiCols && (
              <td style={{ textAlign: 'right' }}>0,00 ₺</td>
            )}
            {showTutar && (
              <td style={{ textAlign: 'right' }}>
                {p.fiyat != null ? formatCurrency(p.fiyat * (p.adet || 1)) : '—'}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const SiparisDetayModal = ({
  siparis,
  onClose,
  onRefresh: _onRefresh,
  isAdmin: _isAdmin = false,
  showFiyat = true,
  nested = false,
}) => {
  const islemGecmisiSorted = useMemo(() => {
    const raw = siparis?.islemGecmisi || [];
    return [...raw].sort((a, b) => {
      const ta = a.tarih ? new Date(a.tarih).getTime() : 0;
      const tb = b.tarih ? new Date(b.tarih).getTime() : 0;
      return ta - tb;
    });
  }, [siparis?.islemGecmisi]);

  if (!siparis) return null;

  const istekPozisyonlari = siparis.istekPozisyonlari || [];
  const onayPozisyonlari = siparis.onayPozisyonlari || [];
  const pozisyonlar = onayPozisyonlari.length > 0 ? onayPozisyonlari : istekPozisyonlari;
  const revizeEdilmis = onayPozisyonlari.length > 0;
  const toplamTutar = siparis.toplamTutar;
  const garantiDahilinde = siparis.garantiDahilinde;
  const konsinye = siparis.konsinye;
  const otomatikTelafi = isGarantiOtomatikTelafiSiparis(siparis);
  const showTutarOzet = showFiyat && !garantiDahilinde && !konsinye && !otomatikTelafi;

  const fotograflar = siparis.fotograflar || [];
  const modalTitle = otomatikTelafi
    ? `Garanti Otomatik Telafi — ${siparis.siparisNumarasi}`
    : `Sipariş Detayı — ${siparis.siparisNumarasi}`;

  const tableProps = {
    garantiDahilinde,
    konsinye,
    otomatikTelafi: showFiyat && otomatikTelafi,
    showFiyat,
  };

  return (
    <div
      className={`modal-overlay siparis-detay-modal${nested ? ' siparis-detay-modal--nested' : ''}`}
      onClick={onClose}
    >
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ fontSize: '16px', fontWeight: 600 }}>{modalTitle}</h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body">
          {otomatikTelafi && (
            <div className="siparis-otomatik-telafi-banner" role="note">
              <strong>Garanti randevu stok telafisi</strong>
              <p>
                Bu belge randevu OTP sırasında otomatik oluşturulmuştur. Stok muhasebesi içindir;
                teknik servise cari borç veya fatura oluşturmaz. Kullanılan parçaların iadesi ayrı
                iade kargo kaydı üzerinden yürür.
              </p>
            </div>
          )}

          <div className="siparis-detay-grid">
            <div className="siparis-detay-field">
              <FieldLabel className="label" as="span">Sipariş No</FieldLabel>
              <span className="value">{siparis.siparisNumarasi}</span>
            </div>
            <div className="siparis-detay-field">
              <FieldLabel className="label" as="span">Teknik Servis</FieldLabel>
              <span className="value">{siparis.teknikServisAdi || '—'}</span>
            </div>
            {(siparis.teknikServisTelefon || siparis.teknikServisYetkiliTelefon) && (
              <div className="siparis-detay-field">
                <FieldLabel className="label" as="span">İletişim</FieldLabel>
                <span className="value">
                  {siparis.teknikServisTelefon || '—'}
                  {siparis.teknikServisYetkiliKisi
                    ? ` · ${siparis.teknikServisYetkiliKisi}${siparis.teknikServisYetkiliTelefon ? ` (${siparis.teknikServisYetkiliTelefon})` : ''}`
                    : (siparis.teknikServisYetkiliTelefon ? ` · ${siparis.teknikServisYetkiliTelefon}` : '')}
                </span>
              </div>
            )}
            <div className="siparis-detay-field">
              <FieldLabel className="label" as="span">Tarih</FieldLabel>
              <span className="value">{formatApiDate(siparis.olusturmaTarihi)}</span>
            </div>
            <div className="siparis-detay-field">
              <FieldLabel className="label" as="span">
                {otomatikTelafi ? 'Belge türü' : 'Garanti Dahilinde'}
              </FieldLabel>
              <span className="value">
                {otomatikTelafi
                  ? getOtomatikTelafiTipLabel(siparis)
                  : (garantiDahilinde ? 'Evet' : 'Hayır')}
              </span>
            </div>
            {showFiyat && otomatikTelafi && (
              <div className="siparis-detay-field">
                <FieldLabel className="label" as="span">Cari bedel</FieldLabel>
                <span className="value">0 ₺ (telafi — borç oluşturmaz)</span>
              </div>
            )}
            {siparis.randevuNumarasi && (
              <div className="siparis-detay-field">
                <FieldLabel className="label" as="span">Randevu No</FieldLabel>
                <span className="value">{siparis.randevuNumarasi}</span>
              </div>
            )}
            {siparis.faturaBilgisi && (
              <div className="siparis-detay-field">
                <FieldLabel className="label" as="span">
                  {siparis.konsinye ? 'Sevk irsaliyesi' : 'Fatura Bilgisi'}
                </FieldLabel>
                <span className="value">{siparis.faturaBilgisi}</span>
              </div>
            )}
            {siparis.kargoFirmasi && (
              <div className="siparis-detay-field">
                <FieldLabel className="label" as="span">Kargo</FieldLabel>
                <span className="value">
                  {siparis.kargoFirmasi} - {siparis.kargoTakipNo}
                </span>
              </div>
            )}
            <div className="siparis-detay-field">
              <FieldLabel className="label" as="span">Teslimat Adresi</FieldLabel>
              <span className="value">{siparis.teslimatAdresi || siparis.teknikServis?.adres || '—'}</span>
            </div>
          </div>

          <SiparisNotuCard not={siparis.not} />

          {fotograflar.length > 0 && (
            <div className="siparis-detay-section">
              <FieldLabel className="siparis-detay-section-title" as="div">Sipariş fotoğrafları</FieldLabel>
              <div className="siparis-detay-foto-grid">
                {fotograflar.map((f, i) => {
                  const href = resolveUploadUrl(f.url);
                  return (
                    <a
                      key={f.dosyaAdi || f.url || i}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="siparis-detay-foto-thumb-wrap"
                    >
                      <img src={href} alt={`Sipariş ${i + 1}`} className="siparis-detay-foto-thumb" loading="lazy" />
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          {revizeEdilmis ? (
            <>
              <div className="siparis-detay-section">
                <FieldLabel className="siparis-detay-section-title" as="div">İstek Pozisyonları (Teknik Servis)</FieldLabel>
                <PozisyonTable pozisyonlar={istekPozisyonlari} {...tableProps} />
              </div>
              <div className="siparis-detay-section">
                <FieldLabel className="siparis-detay-section-title" as="div">Onay Pozisyonları (Revize Edilmiş)</FieldLabel>
                <PozisyonTable pozisyonlar={onayPozisyonlari} {...tableProps} />
                {showFiyat && otomatikTelafi && (
                  <div className="siparis-detay-total">
                    <span>Toplam cari bedel</span>
                    <span>0,00 ₺</span>
                  </div>
                )}
                {showTutarOzet && toplamTutar != null && (
                  <div className="siparis-detay-total">Toplam Tutar: {formatCurrency(toplamTutar)}</div>
                )}
              </div>
            </>
          ) : (
            <div className="siparis-detay-section">
              <FieldLabel className="siparis-detay-section-title" as="div">Pozisyonlar</FieldLabel>
              <PozisyonTable pozisyonlar={pozisyonlar} {...tableProps} />
              {showFiyat && otomatikTelafi && (
                <div className="siparis-detay-total">
                  <span>Toplam cari bedel</span>
                  <span>0,00 ₺</span>
                </div>
              )}
              {showTutarOzet && toplamTutar != null && (
                <div className="siparis-detay-total">Toplam Tutar: {formatCurrency(toplamTutar)}</div>
              )}
            </div>
          )}

          {islemGecmisiSorted.length > 0 && (
            <div className="siparis-detay-section">
              <FieldLabel className="siparis-detay-section-title" as="div">İşlem geçmişi</FieldLabel>
              <ul className="siparis-detay-timeline">
                {islemGecmisiSorted.map((e, idx) => (
                  <li key={idx} className="siparis-detay-timeline-item">
                    <div className="siparis-detay-timeline-dot" />
                    <div className="siparis-detay-timeline-body">
                      <div className="siparis-detay-timeline-time">
                        {toTurkishUpperCase(
                          e.tarih
                            ? new Date(e.tarih).toLocaleString('tr-TR', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '—',
                        )}
                      </div>
                      <div className="siparis-detay-timeline-eylem">
                        {EYLEM_ETIKET[e.eylem] || e.eylem}
                      </div>
                      {(e.oncekiDurum || e.yeniDurum) && (
                        <div className="siparis-detay-timeline-durum">
                          {e.oncekiDurum ? `${e.oncekiDurum}` : '—'}
                          {e.yeniDurum ? ` → ${e.yeniDurum}` : ''}
                        </div>
                      )}
                      {e.kullaniciAdi && (
                        <div className="siparis-detay-timeline-user">{e.kullaniciAdi}</div>
                      )}
                      {e.aciklama && <div className="siparis-detay-timeline-note">{e.aciklama}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose} style={{ fontSize: 13 }}>
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default SiparisDetayModal;
